import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { N8 } from "@/components/BrandMark";
import N8Emblem from "@/components/N8Emblem";

/**
 * HomeN8Guide — the compact Home-facing surface for the same N8 Guide
 * intelligence that used to live on Rituals. Reads the SAME
 * `n8GuideToday` payload already fetched by Home, so there is only ever
 * one underlying N8 Guide system.
 *
 * Editorial, calm, no cards-within-cards: an eyebrow, one warm greeting,
 * one honest observation, then a short list of Today's Priorities that
 * link into the right destination across N8ONE.
 */
export default function HomeN8Guide({ guide, greeting }) {
  const observation =
    guide?.observation ||
    guide?.orientation ||
    guide?.monthly_insight ||
    "";
  const priorities = Array.isArray(guide?.today_priorities)
    ? guide.today_priorities.slice(0, 5)
    : [];

  if (!guide || (priorities.length === 0 && !observation)) return null;

  return (
    <section
      className="reveal reveal-2 relative"
      data-testid="home-n8-guide"
    >
      <div className="flex items-start gap-5">
        {/* N8 Guide emblem — carries the global "breathing = Guide"
            identity rule. The physical orb never moves; only its halo
            + inner sun/path glow breathe at a quiet 7.5s cycle at rest.
            Reduced-motion safe (keyframes defined in index.css). */}
        <N8Emblem
          mode="full"
          size={64}
          breathing
          ariaLabel="N8 Guide emblem"
          testid="home-n8-guide-emblem"
          className="shrink-0 hidden sm:inline-flex mt-1"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
            <Sparkles size={12} />
            <span>Personal Guide · Pro</span>
          </div>
          <h2
            className="mt-3 font-display text-3xl md:text-[38px] tracking-tight leading-[1.06]"
            data-testid="home-n8-guide-title"
          >
            <N8 /> Guide
          </h2>
          <p className="mt-2 font-display italic text-lg text-[hsl(var(--clay))]">
            {greeting || "Good evening"}.
          </p>
          {observation && (
            <p
              className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[hsl(var(--fg))]/85"
              data-testid="home-n8-guide-observation"
            >
              {observation}
            </p>
          )}
        </div>
      </div>

      {priorities.length > 0 && (
        <>
          <div className="mt-8 text-[10.5px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]">
            Today's priorities
          </div>
          <ul
            className="mt-4 divide-y hairline"
            data-testid="home-n8-guide-priorities"
          >
            {priorities.map((p, i) => {
              const title = p.title || p.action || "One intentional choice";
              const note = p.note || p.body || p.why || p.reason || "";
              const to = destinationFor(p);
              const ctaLabel = ctaFor(p);
              return (
                <li
                  key={p.id || i}
                  className="py-4 flex items-start gap-4"
                  data-testid={`home-n8-guide-priority-${i}`}
                >
                  <div className="w-6 shrink-0 pt-1 text-[11px] text-[hsl(var(--copper))] font-medium tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-tight text-[hsl(var(--fg))]">
                      {title}
                    </div>
                    {note && (
                      <p className="mt-1 text-[13.5px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                        {note}
                      </p>
                    )}
                  </div>
                  <Link
                    to={to}
                    data-testid={`home-n8-guide-priority-${i}-cta`}
                    className="shrink-0 inline-flex items-center gap-1 self-center text-[10.5px] uppercase tracking-[0.24em] text-[hsl(var(--copper))] hover:text-[hsl(var(--terracotta))] tab-link"
                  >
                    {ctaLabel} <ArrowRight size={11} />
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

/** Route a priority to the right destination based on its shape. Falls
 *  back to /rituals so a CTA never dead-ends. */
function destinationFor(p) {
  const kind = (p.kind || p.destination || p.category || "").toLowerCase();
  if (kind.includes("journal"))  return "/journal";
  if (kind.includes("mirror"))   return "/mirror";
  if (kind.includes("soul"))     return "/rituals?pillar=soul";
  if (kind.includes("breath"))   return "/rituals?pillar=soul&soul=breath";
  if (kind.includes("nervous"))  return "/rituals?pillar=soul&soul=nervous";
  if (kind.includes("becoming") || kind.includes("progress"))
    return "/becoming?section=choose";
  if (p.ritual_id) return `/rituals?open=${encodeURIComponent(p.ritual_id)}`;
  return "/rituals";
}

function ctaFor(p) {
  const kind = (p.kind || p.destination || p.category || "").toLowerCase();
  if (kind.includes("journal")) return "Reflect on this";
  if (kind.includes("mirror"))  return "Talk it through";
  if (kind.includes("breath"))  return "Begin breathing";
  if (kind.includes("nervous")) return "Support";
  if (kind.includes("soul"))    return "Explore";
  if (kind.includes("becoming") || kind.includes("progress"))
    return "See progress";
  return "Begin ritual";
}
